import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/** Storage wrapper que tenta gravar, e ao falhar por quota, compacta e tenta de novo. */
const safeStorage = createJSONStorage(() => ({
  getItem: (name: string) => localStorage.getItem(name),
  setItem: (name: string, value: string) => {
    try {
      localStorage.setItem(name, value);
    } catch (e: any) {
      const isQuota = e?.name === "QuotaExceededError" || /quota/i.test(String(e?.message));
      if (!isQuota) throw e;
      console.warn("[tasks] localStorage cheio — auto-compactando…");
      try {
        // tenta compactar via state
        const parsed = JSON.parse(value);
        if (parsed?.state?.tasks) {
          // limita 30 tasks mais recentes, 3 outputs cada, sem mediaUrl base64 grande
          const tasks = [...parsed.state.tasks]
            .sort((a: any, b: any) => b.updatedAt - a.updatedAt)
            .slice(0, 30)
            .map((t: any) => ({
              ...t,
              outputs: (t.outputs ?? []).slice(-3).map((o: any) => ({
                ...o,
                mediaUrl: o.mediaUrl?.startsWith("data:") && o.mediaUrl.length > 5000
                  ? undefined : o.mediaUrl,
                text: o.text?.length > 4000 ? o.text.slice(0, 4000) + "\n…[truncado pelo auto-compactador]" : o.text
              }))
            }));
          const compact = { ...parsed, state: { ...parsed.state, tasks } };
          const shrunk = JSON.stringify(compact);
          try {
            localStorage.setItem(name, shrunk);
            console.warn(`[tasks] auto-compactado: ${value.length} → ${shrunk.length} bytes (${tasks.length} tasks mantidas)`);
            // Alerta visível
            if (typeof window !== "undefined") {
              setTimeout(() => alert(
                "⚠ localStorage estourou. Auto-compactei suas tasks (manti as 30 mais recentes, 3 outputs/cada).\n\n" +
                "Pra não perder dados, ligue o Cloud Sync em ⚙️ Configurações → ↑ ENVIAR — assim tudo fica no DB."
              ), 100);
            }
          } catch (e2) {
            // ainda não cabe — limpar drasticamente
            localStorage.removeItem(name);
            console.error("[tasks] mesmo compactado não coube — storage limpo. ", e2);
            if (typeof window !== "undefined") {
              setTimeout(() => alert(
                "🛑 localStorage de tasks foi LIMPO porque mesmo compactado não coube.\n" +
                "Se você tinha trabalho importante, desfaça com 'Importar JSON' se tiver backup.\n" +
                "URGENTE: ligue Cloud Sync em ⚙️."
              ), 100);
            }
          }
        } else {
          throw e;
        }
      } catch (e3) {
        console.error("[tasks] falha ao auto-compactar:", e3);
        throw e;
      }
    }
  },
  removeItem: (name: string) => localStorage.removeItem(name)
}));

export type TaskStatus = "pending" | "in-progress" | "review" | "done" | "blocked";
export type TaskPriority = "low" | "normal" | "high" | "urgent";
export type TaskComplexity = "trivial" | "low" | "medium" | "high" | "critical";
export type TaskStage =
  | "draft"              // Marcio digitou, ainda não delegou
  | "delegated"          // designado, agente vai executar
  | "in-progress"        // execução em curso
  | "supervisor-review"  // supervisor do depto avalia
  | "client-approval"    // Marcio aprova final
  | "delivered"          // entregue
  | "blocked"            // bloqueada
  | "rejected";          // rejeitada

export type ReferenceKind = "image" | "audio" | "video" | "link" | "file";

export interface TaskReference {
  id: string;
  kind: ReferenceKind;
  url: string;              // dataURL ou URL externa
  name?: string;
  notes?: string;
}

export interface Approval {
  id: string;
  stage: TaskStage;
  by: string;               // personaId ou "marcio"
  decision: "approved" | "rejected" | "needs-revision";
  at: number;
  notes?: string;
}

export type OutputKind = "text" | "image" | "video";

export interface TaskOutput {
  id: string;
  by: string;            // personaId
  at: number;
  text: string;
  kind?: OutputKind;
  /** data URL ou URL externa */
  mediaUrl?: string;
  /** provider que gerou (gemini/openai/higgsfield/claude) */
  provider?: string;
  /** modelo usado */
  model?: string;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  /** cliente a quem a demanda pertence */
  clientId?: string;
  /** departamentos-alvo (ex: ["copy"] ou ["copy","criativo"]) */
  targetDepts: string[];
  /** ids específicos de agentes designados (opcional) */
  assignedTo: string[];
  status: TaskStatus;
  priority: TaskPriority;
  /** complexidade — alimenta o router de modelo de IA */
  complexity?: TaskComplexity;
  /** estágio do fluxo de aprovação */
  stage?: TaskStage;
  /** estimativa em horas (gerada por IA ou manual) */
  estimatedHours?: number;
  /** referências (imagens, áudios, vídeos, links) */
  references?: TaskReference[];
  /** histórico de aprovações/rejeições */
  approvals?: Approval[];
  /** flag: agente pediu convocação de reunião pra apresentar */
  presentationRequested?: boolean;
  /** se setado: ao entregar, criar follow-up nessas áreas */
  handoffToDepts?: string[];
  /** se essa task foi criada via handoff, aponta pra task original */
  handoffFrom?: string;
  /** demandas usam tags pra matching de skills */
  tags: string[];
  createdAt: number;
  updatedAt: number;
  outputs: TaskOutput[];
}

interface TasksState {
  tasks: Task[];
  create: (t: Omit<Task, "id" | "createdAt" | "updatedAt" | "outputs" | "status"> & { status?: TaskStatus }) => string;
  update: (id: string, patch: Partial<Task>) => void;
  remove: (id: string) => void;
  addOutput: (taskId: string, by: string, text: string, extra?: Partial<TaskOutput>) => void;
  setStatus: (id: string, status: TaskStatus) => void;
  setStage: (id: string, stage: TaskStage) => void;
  assign: (id: string, personaIds: string[]) => void;
  addReference: (id: string, ref: Omit<TaskReference, "id">) => void;
  removeReference: (id: string, refId: string) => void;
  addApproval: (id: string, app: Omit<Approval, "id" | "at">) => void;
  requestPresentation: (id: string) => void;
  /** Remove outputs antigos pra reduzir tamanho do localStorage */
  compact: (opts?: { maxOutputsPerTask?: number; truncateOutputCharsAbove?: number; maxTasks?: number }) => { freed: number; tasksAffected: number };
  /** Apaga tudo (cuidado) */
  clearAll: () => void;
}

const uid = () => Math.random().toString(36).slice(2, 10);

export const useTasks = create<TasksState>()(
  persist(
    (set) => ({
      tasks: [],
      create: (t) => {
        const id = uid();
        set(s => ({
          tasks: [{
            ...t,
            id,
            status: t.status ?? "pending",
            outputs: [],
            createdAt: Date.now(),
            updatedAt: Date.now()
          }, ...s.tasks]
        }));
        return id;
      },
      update: (id, patch) => set(s => ({
        tasks: s.tasks.map(t => t.id === id ? { ...t, ...patch, updatedAt: Date.now() } : t)
      })),
      remove: (id) => set(s => ({ tasks: s.tasks.filter(t => t.id !== id) })),
      addOutput: (taskId, by, text, extra) => set(s => ({
        tasks: s.tasks.map(t => t.id === taskId
          ? { ...t, outputs: [...t.outputs, { id: uid(), by, at: Date.now(), text, kind: "text", ...extra }], updatedAt: Date.now() }
          : t)
      })),
      setStatus: (id, status) => set(s => ({
        tasks: s.tasks.map(t => t.id === id ? { ...t, status, updatedAt: Date.now() } : t)
      })),
      setStage: (id, stage) => set(s => ({
        tasks: s.tasks.map(t => t.id === id ? { ...t, stage, updatedAt: Date.now() } : t)
      })),
      assign: (id, assignedTo) => set(s => ({
        tasks: s.tasks.map(t => t.id === id ? { ...t, assignedTo, updatedAt: Date.now() } : t)
      })),
      addReference: (id, ref) => set(s => ({
        tasks: s.tasks.map(t => t.id === id
          ? { ...t, references: [...(t.references ?? []), { ...ref, id: uid() }], updatedAt: Date.now() }
          : t)
      })),
      removeReference: (id, refId) => set(s => ({
        tasks: s.tasks.map(t => t.id === id
          ? { ...t, references: (t.references ?? []).filter(r => r.id !== refId), updatedAt: Date.now() }
          : t)
      })),
      addApproval: (id, app) => set(s => ({
        tasks: s.tasks.map(t => t.id === id
          ? { ...t,
              approvals: [...(t.approvals ?? []), { ...app, id: uid(), at: Date.now() }],
              stage: app.decision === "approved"
                ? (app.stage === "supervisor-review" ? "client-approval"
                  : app.stage === "client-approval" ? "delivered"
                  : t.stage)
                : app.decision === "rejected" ? "rejected"
                : t.stage,
              updatedAt: Date.now() }
          : t)
      })),
      requestPresentation: (id) => set(s => ({
        tasks: s.tasks.map(t => t.id === id ? { ...t, presentationRequested: true, updatedAt: Date.now() } : t)
      })),

      compact: (opts) => {
        const maxOut = opts?.maxOutputsPerTask ?? 5;
        const truncAbove = opts?.truncateOutputCharsAbove ?? 8000;
        const maxTasks = opts?.maxTasks ?? 60;
        let freed = 0;
        let tasksAffected = 0;
        set(s => {
          const before = JSON.stringify(s.tasks).length;
          // 1. Limita tasks ao N mais recentes
          let tasks = [...s.tasks].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, maxTasks);
          // 2. Pra cada task, limita outputs e trunca os grandes
          tasks = tasks.map(t => {
            const outs = (t.outputs ?? []).slice(-maxOut).map(o => {
              if (o.mediaUrl && o.mediaUrl.startsWith("data:") && o.mediaUrl.length > truncAbove) {
                // imagens base64 muito grandes — descartar dataURL, deixar marker
                return { ...o, mediaUrl: undefined, text: (o.text || "") + "\n\n[mídia descartada na compactação]" };
              }
              if (o.text && o.text.length > truncAbove) {
                return { ...o, text: o.text.slice(0, truncAbove) + "\n…[truncado]" };
              }
              return o;
            });
            if (outs.length !== (t.outputs?.length ?? 0)) tasksAffected++;
            return { ...t, outputs: outs };
          });
          const after = JSON.stringify(tasks).length;
          freed = before - after;
          return { tasks };
        });
        return { freed, tasksAffected };
      },

      clearAll: () => set({ tasks: [] })
    }),
    { name: "agencia-vpc-tasks-v1", storage: safeStorage }
  )
);
